import type {FC} from 'react';
import {useState} from 'react';
import {GridActionsCellItem} from '@mui/x-data-grid';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import type {Member} from 'api/v2';
import {Icon, IconNames, Tooltip} from '@wellstone-solutions/web';
import {Api} from '@wellstone-solutions/common';
import {membersQueryKeys} from 'queries/members';
import {ConfirmDialog} from 'components/ConfirmDialog';
import {useStores} from 'hooks/useStores';

interface RemoveStaffButtonProps {
  member: Member;
}

export const RemoveStaffButton: FC<RemoveStaffButtonProps> = ({member}) => {
  const {meStore} = useStores();
  const [dialogOpen, setDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const removeStaffMutation = useMutation({
    mutationFn: () => {
      return Api.Instance.current().delete(
        `orgs/${meStore.activeMembership.organization.id}/staff/${member.id}`,
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: membersQueryKeys.lists(),
      });

      setDialogOpen(false);
    },
  });

  return (
    <>
      <GridActionsCellItem
        label="Remove Staff"
        icon={
          <Tooltip title="Remove Staff">
            <div>
              <Icon name={IconNames.Trash} />
            </div>
          </Tooltip>
        }
        showInMenu={false}
        onClick={() => setDialogOpen(true)}
      />

      <ConfirmDialog
        open={dialogOpen}
        title="Remove Staff"
        text={`Are you sure you want to remove ${member.name} from your organization? This cannot be undone.`}
        buttonLabel="Remove"
        onConfirm={() => {
          removeStaffMutation.mutate();
        }}
        onClose={() => setDialogOpen(false)}
        loading={removeStaffMutation.isPending}
      />
    </>
  );
};
